import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Vg2Shell } from "./Vg2Shell";
import { fmtEur } from "@/lib/vg2Format";
import { generateDecision, getDecision } from "@/lib/vg2Decidi";
import { chooseOperation, Economics, resultLine } from "@/components/vg2/RequestedOperations";
import { InsightBlock, useInsight } from "@/components/vg2/DecidiInsight";

const R = (a) => (a ? (a[0] === a[2] ? fmtEur(a[0]) : `${fmtEur(a[0])} – ${fmtEur(a[2])}`) : "—");
const signed = (v) => `${v >= 0 ? "+" : "−"}${fmtEur(Math.abs(v))}`;

function Block({ title, tid, children }) {
  return (
    <section className="mb-8" data-testid={tid}>
      <h2 className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">{title}</h2>
      <div className="rounded-xl border border-vg2-line bg-white p-4 md:p-5 text-sm">{children}</div>
    </section>
  );
}

function Line({ label, value, tid, strong }) {
  return (
    <div className="flex justify-between gap-4 py-1" data-testid={tid}>
      <span className="text-vg2-muted">{label}</span>
      <span className={`whitespace-nowrap ${strong ? "font-semibold" : ""}`}>{value}</span>
    </div>
  );
}

function Investment({ m, buy }) {
  const works = m.capital_required || m.capital_invested_works || m.works;
  const worksOnly = m.works || works;
  const startup = m.kind === "income" && m.costs && m.costs[2] > 0 && (!worksOnly || m.costs[2] > worksOnly[2]);
  if (m.kind === "none") {
    return <Block title="Investimento richiesto" tid="analizza-investment"><p data-testid="analizza-investment-none">Non stimabile con i dati attuali.</p></Block>;
  }
  return (
    <Block title="Investimento richiesto" tid="analizza-investment">
      {startup ? <Line label={works ? "Investimento iniziale (lavori e avvio)" : "Costi di avvio"} value={R(m.costs)} tid="analizza-investment-works" strong />
        : works ? <Line label="Capitale da investire nei lavori" value={R(works)} tid="analizza-investment-works" strong />
        : <p data-testid="analizza-investment-none">Nessun investimento in lavori: si parte dall'immobile così com'è.</p>}
      {m.capital_in_property_as_is && (
        <Line label={buy ? "Prezzo di riferimento (valore di mercato stimato, centrale)" : "Capitale che resta nell'immobile (valore attuale, centrale)"}
              value={fmtEur(m.capital_in_property_as_is[1])} tid="analizza-investment-property" />
      )}
      {m.works_component?.method && (
        <p className="text-xs text-vg2-muted mt-2" data-testid="analizza-works-method">
          Stima lavori: {m.works_component.method}
          {m.works_component.costs ? ` = ${fmtEur(m.works_component.costs[0])} – ${fmtEur(m.works_component.costs[1])}` : ""}
          {m.works_component.exploratory ? " · esplorativa" : ""}
        </p>
      )}
      {buy && (
        <p className="text-xs text-amber-800 mt-2" data-testid="analizza-buy-note">
          Acquisto: prezzo effettivo, imposte e costi notarili non sono ancora calcolati. Il risultato è esplorativo.
        </p>
      )}
    </Block>
  );
}

const isBuy = (card) => (card.feasibility_notes || []).some((n) => n.startsWith("Acquisto:"));

const DENOM = (d) => (d ? (d.includes("lavori") ? "valore attuale dell'immobile più i lavori" : "valore attuale dell'immobile") : null);

function Outcome({ m, buy }) {
  if (m.kind === "sale" && m.vs_sell_now) {
    return (
      <Block title="Risultato rispetto a vendere subito · capitale una tantum" tid="analizza-outcome">
        <Line label="Vendere subito, ricavo netto" value={R(m.vs_sell_now.sell_now_net)} tid="analizza-outcome-sellnow" />
        <Line label="Differenza rispetto a vendere subito (stima centrale)" value={signed(m.vs_sell_now.incremental_net[1])} tid="analizza-outcome-incremental" strong />
        <p className="text-xs text-vg2-muted" data-testid="analizza-outcome-incremental-range">
          Secondo le ipotesi va da {signed(m.vs_sell_now.incremental_net[0])} (caso sfavorevole) a {signed(m.vs_sell_now.incremental_net[2])} (caso favorevole): è una stima, non un risultato certo.
        </p>
        {m.break_even_sale_price && (
          <p className="text-xs text-vg2-muted mt-2" data-testid="analizza-outcome-breakeven">
            Per rendere quanto vendere subito, dopo i lavori l'immobile deve vendersi ad almeno {fmtEur(m.break_even_sale_price)}.
          </p>
        )}
      </Block>
    );
  }
  if (m.kind === "income" && (m.yield_on_total_pct != null || m.short_term)) {
    const den = DENOM(m.yield_denominator)?.replace(...(buy ? ["valore attuale dell'immobile", "valore di mercato stimato dell'immobile (prezzo di riferimento)"] : ["", ""]));
    return (
      <Block title="Rendimento · reddito ricorrente" tid="analizza-outcome">
        {m.yield_on_total_pct != null && <Line label="Rendimento netto annuo stimato" value={`${m.yield_on_total_pct.toLocaleString("it-IT")}%`} tid="analizza-outcome-yield" strong />}
        {m.payback_total_years != null && <Line label="Tempo indicativo di recupero del capitale" value={`circa ${Math.round(m.payback_total_years)} anni`} tid="analizza-outcome-payback" />}
        {den && (
          <p className="text-xs text-vg2-muted mt-2" data-testid="analizza-outcome-denominator">
            Calcolati sul {den}, cioè il capitale che resta impegnato. Il reddito è un'entrata annua e non si somma al capitale di una vendita{buy ? ": l'immobile resterebbe tuo dopo l'acquisto." : ": l'immobile resta tuo."}
          </p>
        )}
        {m.short_term && <ShortTermConditions m={m} />}
      </Block>
    );
  }
  return null;
}

const ROOMS = { 1: "1 camera", 2: "2 camere", "3+": "3 o più camere" };

// §V3-STR · condizioni che rendono raggiungibile il centrale (dai numeri effettivi del motore).
function ShortTermConditions({ m }) {
  const s = m.short_term;
  const rooms = ROOMS[s.bedroom_band];
  return (
    <div className="mt-3 text-sm text-vg2-ink space-y-1" data-testid="analizza-st-conditions">
      <p data-testid="analizza-st-central">
        Il valore centrale di <span className="whitespace-nowrap">{fmtEur(m.annual_net[1])}</span> netti l'anno presuppone
        {s.nights ? <> circa {s.nights} notti occupate ({s.occupancy_pct}% dell'anno)</> : null}
        {s.adr ? <> a circa <span className="whitespace-nowrap">{fmtEur(s.adr)}</span> per notte</> : null}
        {rooms ? <>, per un alloggio con {rooms}</> : null}; gestione, commissioni, pulizie e imposte assorbono circa il {String(s.cost_share_pct).replace(".", ",")}% dei ricavi.
      </p>
      <p className="text-vg2-muted" data-testid="analizza-st-downside">
        Meno notti occupate o una tariffa più bassa portano il risultato verso <span className="whitespace-nowrap">{fmtEur(m.annual_net[0])}</span>.
        {s.exploratory && " I dati locali sono limitati: è una stima esplorativa."}
      </p>
    </div>
  );
}

const Amt = ({ v }) => <span className="whitespace-nowrap">{fmtEur(Math.abs(v))}</span>;

function compareText(m, om) {
  if (m.kind === "sale" && om.kind === "sale") {
    const d = m.net[1] - om.net[1];
    return <><Amt v={d} /> {d >= 0 ? "in più" : "in meno"} di ricavo netto (stima centrale)</>;
  }
  if (m.kind === "income" && om.kind === "income") {
    const d = m.annual_net[1] - om.annual_net[1];
    return <><Amt v={d} /> l'anno {d >= 0 ? "in più" : "in meno"} di reddito netto (stima centrale)</>;
  }
  const mine = m.kind === "sale" ? "porta capitale una tantum" : m.kind === "income" ? "porta reddito ricorrente" : "combina capitale e reddito";
  return <>grandezze diverse, non sommabili: questa {mine}, l'altra {resultLine(om)}</>;
}

function Compare({ card, others }) {
  const m = card.analysis_metrics || {};
  if (m.kind === "none") return null;
  // Vendere subito è già il termine di confronto del blocco «Risultato»: non lo ripetiamo.
  const rows = others.filter((o) => !(m.vs_sell_now && o.scenario_key === "SELL_NOW")).map((o) => {
    const om = o.analysis_metrics || {};
    if (om.kind === "none") return { key: o.scenario_key, title: o.title, text: "non ancora calcolabile, quindi non confrontabile" };
    const tm = m.time_months && om.time_months ? (m.time_months[1] > om.time_months[1] ? " · richiede più tempo" : m.time_months[1] < om.time_months[1] ? " · richiede meno tempo" : "") : "";
    const inv = (m.capital_required || m.capital_invested_works) && !(om.capital_required || om.capital_invested_works) ? " · richiede lavori" : "";
    return { key: o.scenario_key, title: o.title, text: <>{compareText(m, om)}{tm}{inv}</> };
  });
  if (!rows.length) return null;
  return (
    <Block title="Rispetto alle altre operazioni che hai chiesto" tid="analizza-compare">
      <ul className="space-y-2">
        {rows.map((r, i) => <li key={r.key} data-testid={`analizza-compare-${i}`}>Rispetto a <strong>{r.title}</strong>: {r.text}</li>)}
      </ul>
    </Block>
  );
}

// Solo 1–2 condizioni decisive (dal backend); le note tecniche restano nei dati salvati.
function Checks({ card, checks }) {
  const items = checks || [];
  if (!items.length && card.feasibility !== "vincolo") return null;
  return (
    <Block title="Cosa verificare prima di procedere" tid="analizza-checks">
      {card.feasibility === "vincolo" && (
        <p className="flex items-center gap-1.5 text-amber-800 mb-2" data-testid="analizza-constraint"><AlertTriangle size={15} /> Vincolo da superare prima di procedere.</p>
      )}
      {items.length > 0 && (
        <ul className="list-disc pl-5 space-y-1 text-vg2-ink">
          {items.map((c) => <li key={c.key} data-testid={`analizza-check-${c.key}`}>{c.text}</li>)}
        </ul>
      )}
    </Block>
  );
}

export default function Analizza() {
  const { caseId, scenarioKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Da DECIDI arriva lo stesso confronto già mostrato: nessuna nuova lettura necessaria.
  const passed = location.state?.view;
  const initial = passed && passed.case_id === caseId && !passed.structured_request_ref?.stale ? passed : null;
  const [view, setView] = React.useState(initial);
  const [err, setErr] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);
  React.useEffect(() => {
    if (initial && attempt === 0) return undefined;
    let alive = true;
    (async () => {
      setErr(null);
      // Lettura con ritentativi: un errore temporaneo (rete, riavvio del servizio) non deve
      // diventare «Analisi non disponibile» né provocare una nuova generazione.
      const read = async () => {
        for (let i = 0; i < 4; i += 1) {
          try { return await getDecision(caseId); } catch (e) {
            const s = e?.response?.status;
            if (s === 404) return null;
            if (s && s < 500 && s !== 429) throw e;
            await new Promise((r) => setTimeout(r, 800 * (i + 1)));
          }
        }
        throw new Error("decision-unreachable");
      };
      try {
        let v = await read();
        if (!v || v?.structured_request_ref?.stale) {
          // Solo se la decisione manca o è superata dalla richiesta: stessa logica di DECIDI.
          try { await generateDecision(caseId, `d-inline-${caseId}`); } catch (ge) {
            if (ge?.response && ge.response.status < 500) throw ge;
          }
          v = await read();
        }
        if (alive) setView(v);
      } catch (e) {
        if (alive) setErr(e?.response?.status === 401 || e?.response?.status === 404
          ? "Questa analisi appartiene a una sessione diversa: aprila dal dispositivo con cui hai fatto la richiesta."
          : "Analisi non disponibile in questo momento.");
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, attempt]);
  const _k = String(scenarioKey || "").toLowerCase();
  const card = view?.scenarios?.find((c) => c.scenario_key === scenarioKey)
    || view?.scenarios?.find((c) => String(c.scenario_key).toLowerCase() === _k || c.strategy_category === _k);
  const insight = useInsight(caseId, view?.decision_result_id, card?.scenario_key, !!card);
  const proceed = async () => {
    setBusy(true); setErr(null);
    try {
      if (view.owner_decision?.selected_scenario_key !== card.scenario_key) await chooseOperation(view, caseId, card.scenario_key);
      navigate(`/vg2/agisci/${caseId}`);
    } catch (e) {
      // Due generazioni ravvicinate: la vista aperta può essere superata da una versione più recente → riallinea e riprova una volta.
      if (e?.response?.status === 409) {
        try {
          const fresh = await getDecision(caseId);
          if ((fresh.scenarios || []).some((c) => c.scenario_key === card.scenario_key)) {
            await chooseOperation(fresh, caseId, card.scenario_key);
            navigate(`/vg2/agisci/${caseId}`);
            return;
          }
        } catch { /* messaggio sotto */ }
      }
      setErr("Non riesco a registrare la scelta: riprova."); setBusy(false);
    }
  };
  if (!view && !err) return <Vg2Shell><div className="py-24 flex justify-center"><Loader2 className="animate-spin" /></div></Vg2Shell>;
  if (!card) return (
    <Vg2Shell>
      <div className="py-24" data-testid="analizza-missing">
        <p data-testid="analizza-missing-message">{err || "Questa operazione non è nel confronto attuale: torna a DECIDI e scegli tra quelle calcolate."}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          {err && (
            <button type="button" onClick={() => { setView(null); setAttempt((a) => a + 1); }} data-testid="analizza-retry"
                    className="rounded-full bg-vg2-red text-white px-4 py-2 text-sm hover:opacity-90 transition-opacity">Riprova</button>
          )}
          <button type="button" onClick={() => navigate(`/vg2/decidi/${caseId}`)} data-testid="analizza-missing-back"
                  className="rounded-full border border-vg2-line px-4 py-2 text-sm hover:border-vg2-red transition-colors">Torna a DECIDI</button>
        </div>
      </div>
    </Vg2Shell>
  );
  const m = card.analysis_metrics || {};
  const others = view.scenarios.filter((c) => c.request_origin === "requested" && c.scenario_key !== card.scenario_key);
  return (
    <Vg2Shell>
      <div className="max-w-[820px] mx-auto px-4 py-10" data-testid="analizza-page">
        <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">Analizza · mi conviene?</p>
        <h1 className="font-display font-semibold text-3xl md:text-4xl leading-tight mb-2" data-testid="analizza-title">{card.title}</h1>
        <p className="text-sm text-vg2-muted mb-8" data-testid="analizza-origin">
          {card.request_origin === "requested" ? `Operazione che hai chiesto («${card.request_label}»)` : "Alternativa suggerita da ValutaGenio"} · versione {view.structured_request_ref?.version} della richiesta · stessi numeri del confronto in DECIDI
        </p>
        <InsightBlock state={insight} decisionResultId={view.decision_result_id} tid="analizza-insight" />
        <Investment m={m} buy={isBuy(card)} />
        {m.kind !== "none" && (
          <Block title={m.kind === "income" ? "Reddito e costi previsti · stime annue" : "Ricavi e costi previsti · stime"} tid="analizza-economics">
            <Economics e={card.economic_analysis} tid="analizza-econ" hideInvest flush />
          </Block>
        )}
        <Outcome m={m} buy={isBuy(card)} />
        <Compare card={card} others={others} />
        <Checks card={card} checks={insight.data?.checks} />
        {err && <p className="text-sm text-vg2-error mb-3" data-testid="analizza-error">{err}</p>}
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={proceed} disabled={busy || m.kind === "none"} data-testid="analizza-proceed"
                  className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm bg-vg2-red text-white disabled:opacity-60">
            <ArrowRight size={15} /> Scelgo questa operazione e passo ad AGISCI
          </button>
          <button type="button" onClick={() => navigate(`/vg2/decidi/${caseId}`)} data-testid="analizza-back"
                  className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm border border-vg2-line bg-white">
            <ArrowLeft size={15} /> Torna al confronto
          </button>
        </div>
      </div>
    </Vg2Shell>
  );
}
